import { Fragment, useEffect, useState } from "react";
import {
	formatInstanceSize,
	getEnvironmentInstanceSizes,
	type EnvironmentInstanceSizeInfo,
} from "@/lib/api";
import { formatCpuRange, formatMemory } from "@/lib/format-k8s-quantity";
import { useBillingRates } from "@/lib/use-billing-rates";
import {
	CREDIT_EXPIRY_DAYS,
	formatRateCredits,
	groupRatesByCategory,
	groupRatesByModelTab,
} from "@/lib/billing";
import {
	DocHeading,
	DocList,
	DocPage,
	DocParagraph,
	DocRateTable,
	DocScrollTable,
	DocSubheading,
	DocTabs,
} from "../DocElements";

// 単価そのものは API から取るので、ここには各カテゴリが何に対する料金かだけを書く
const CATEGORY_DESCRIPTIONS: Readonly<Record<string, string>> = {
	modelApi: "エージェントがLLMに送った入力と、LLMが生成した出力のトークン数に応じて課金されます。",
	agentRuntime:
		"エージェントがチャットの依頼に取り組んでいる時間に応じて課金されます。エージェントがあなたの返信を待っている間は課金されません。",
	environmentServe:
		"エージェントを起動している時間に応じて課金されます。単価はインスタンスサイズごとに異なります。起動や停止の処理中も起動中として計算され、停止中は課金されません。",
	gpu: "学習ジョブでGPUを使った時間に応じて課金されます。単価はGPUの構成ごとに異なります。",
	storage:
		"エージェントのディスク（環境ルートボリューム）は、確保している容量と時間に応じて課金されます。エージェントを停止していても、起動中と同じ単価で課金されます。共有ボリュームは、保存しているデータの容量と読み書きした量に応じて課金されます。",
	webSearch: "エージェントがWeb検索をした回数に応じて課金されます。",
};

export function PricingPage({ active }: { active: boolean }) {
	const { rates, error } = useBillingRates(active);

	return (
		<DocPage title="料金">
			<DocParagraph>
				Izanagiの料金は「クレジット」で計算されます。LLMのトークン数やエージェントの稼働時間など、使った分だけクレジットが消費されます。
			</DocParagraph>

			<DocHeading>クレジット</DocHeading>
			<DocParagraph>
				クレジットはご契約に応じて組織に付与され、組織内のすべてのプロジェクト・エージェントの利用で共通して消費されます。
			</DocParagraph>
			<DocList>
				<li>消費は、古く付与されたクレジットから順に充当されます。</li>
				<li>付与されたクレジットの未使用分は、付与から{CREDIT_EXPIRY_DAYS}日後に失効します。</li>
			</DocList>

			<DocHeading>単価</DocHeading>
			<DocParagraph>
				いま適用されている単価です。単価が改定されても、すでに消費したクレジットは消費した時点の単価で計算されています。
			</DocParagraph>
			{error !== null && <DocParagraph>{error}</DocParagraph>}
			{error === null && rates === null && <DocParagraph>読み込み中…</DocParagraph>}
			{rates !== null &&
				groupRatesByCategory(rates).map(({ category, groups }) => (
					<Fragment key={category.id}>
						<DocSubheading>{category.label}</DocSubheading>
						{CATEGORY_DESCRIPTIONS[category.id] !== undefined && (
							<DocParagraph>{CATEGORY_DESCRIPTIONS[category.id]}</DocParagraph>
						)}
						{category.id === "modelApi" ? (
							<DocTabs
								tabs={groupRatesByModelTab(groups).map((tab) => ({
									key: tab.key,
									label: tab.label,
									content: <DocRateTable groups={tab.groups} />,
								}))}
							/>
						) : category.id === "environmentServe" ? (
							<EnvironmentServeRates />
						) : (
							<DocRateTable groups={groups} />
						)}
					</Fragment>
				))}
		</DocPage>
	);
}

/**
 * サーバー基本料金。単価はサイズの数だけ行があるので、サイズ選択と同じ
 * /api/environments/instance-sizes (単価は計上と同じ規則で解決済み) から GPU の構成と並べた表にする。
 */
function EnvironmentServeRates() {
	const [sizes, setSizes] = useState<EnvironmentInstanceSizeInfo[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const controller = new AbortController();
		getEnvironmentInstanceSizes(controller.signal)
			.then(setSizes)
			.catch((err: Error) => {
				if (!controller.signal.aborted) setError(err.message);
			});
		return () => controller.abort();
	}, []);

	return (
		<>
			{error !== null && <DocParagraph>{error}</DocParagraph>}
			{error === null && sizes === null && <DocParagraph>読み込み中…</DocParagraph>}
			{sizes !== null && (
				<DocScrollTable
					columns={[
						{ label: "名前" },
						{ label: "GPU" },
						{ label: "課金（クレジット / 時間）", align: "right" },
					]}
					rows={sizes
						.filter((info) => !info.deviceOnly)
						.map((info) => ({
							key: info.size,
							cells: [
								`${formatInstanceSize(info.size)}（${formatCpuRange(info.cpuRequest, info.cpuLimit)} / ${formatMemory(info.memory)}）`,
								info.gpu === null
									? "なし"
									: `${info.gpu.name} ×${info.gpu.count}（${info.gpu.totalMemoryGb} GB）`,
								formatRateCredits(info.creditsPerHour),
							],
						}))}
				/>
			)}
		</>
	);
}
